// MigrationCrunch — pair-specific SEO landing pages.
// ACT! is the universal source. Each entry maps ACT! → a destination CRM
// with hand-written copy targeting the exact search query a defector would
// type into Google ("act to salesforce migration", "export act database to
// hubspot", etc.).
//
// Adding a pair? Just append to this object. The route /migrate/:pair
// looks it up automatically — no React changes needed.
//
// Notes on the schema:
//   slug          – URL path segment ("act-to-hubspot")
//   sourceId      – matches SOURCE_CRMS in MigrationCrunchPage ('act' here)
//   destId        – matches a preset id from migration_engine PRESETS
//                   ('salesforce', 'hubspot', 'pipedrive', 'zoho', etc.)
//                   Pre-selects that destination after upload.
//   headline      – H1, contains target keyword verbatim
//   subhead       – 1-paragraph value prop
//   painPoint     – the specific data-loss risk for THIS pair
//   ourEdge       – what our patent-pending AI does that others don't
//   keywords      – array of search queries this page should rank for
//   estimatedTime – "7 minutes", "12 minutes" etc. — used in copy
//   seoTitle      – <title> tag, ≤60 chars
//   seoDescription– <meta description>, ≤155 chars

export const MIGRATION_PAIRS = {
  'act-to-salesforce': {
    slug: 'act-to-salesforce',
    sourceId: 'act', sourceName: 'ACT!',
    destId: 'salesforce', destName: 'Salesforce',
    headline: 'ACT! to Salesforce migration — keep every note, every relationship.',
    subhead:
      "Switching from ACT! to Salesforce shouldn't mean abandoning 20 years of sales history. " +
      "Our patent-pending AI extracts the names, companies, and contact references buried inside " +
      "your ACT! notes and re-links them as Salesforce activity records on the other side. " +
      "Lead vs Contact split? Handled automatically.",
    painPoint:
      "ACT! stores notes as rich-text blobs with embedded contact references. " +
      "Most migration tools dump that text into Salesforce's Description field — " +
      "and silently break every cross-link to other contacts and opportunities.",
    ourEdge:
      "We extract the relationships INSIDE your ACT! notes pre-migration, then re-associate " +
      "them to the correct Salesforce Lead / Contact / Opportunity records post-migration. " +
      "Your CFO's mention of \"the Acme deal\" still links to the actual Acme record.",
    keywords: ['act to salesforce migration', 'export act database to salesforce', 'act crm to salesforce', 'migrate act contacts to salesforce', 'sage act to salesforce'],
    estimatedTime: '7 minutes',
    seoTitle: 'ACT! to Salesforce Migration — AI-Powered | MigrationCrunch',
    seoDescription: 'Migrate ACT! to Salesforce in 7 minutes. Patent-pending AI re-links contact references inside notes. Lead/Contact split handled. From $599.',
  },
  'act-to-hubspot': {
    slug: 'act-to-hubspot',
    sourceId: 'act', sourceName: 'ACT!',
    destId: 'hubspot', destName: 'HubSpot',
    headline: 'ACT! to HubSpot migration — without losing 20 years of notes.',
    subhead:
      "HubSpot's Companies-Contacts-Deals model doesn't match ACT! one-to-one. " +
      "Our AI maps your ACT! contacts to HubSpot Contacts, your ACT! companies to HubSpot " +
      "Companies, and re-attaches every historical note to the right record — including " +
      "the cross-references buried in the prose.",
    painPoint:
      "Generic CSV exports drop your ACT! note history into HubSpot's generic \"Notes\" engagement " +
      "type — but the names mentioned inside those notes become orphan text. " +
      "Your team can't search by them; HubSpot's reporting can't see them.",
    ourEdge:
      "Our AI extracts every person, company, email, and phone reference INSIDE your ACT! notes " +
      "and re-creates them as proper HubSpot activity associations. Search \"Mary Chen\" in HubSpot " +
      "and you'll find every meeting she was ever mentioned in.",
    keywords: ['act to hubspot migration', 'export act to hubspot', 'act crm hubspot import', 'migrate sage act to hubspot', 'act contacts to hubspot'],
    estimatedTime: '6 minutes',
    seoTitle: 'ACT! to HubSpot Migration — AI Note Recovery | MigrationCrunch',
    seoDescription: 'Move from ACT! to HubSpot without losing note history. Patent-pending AI re-links contact references. Companies + Contacts + Deals model handled. From $599.',
  },
  'act-to-pipedrive': {
    slug: 'act-to-pipedrive',
    sourceId: 'act', sourceName: 'ACT!',
    destId: 'pipedrive', destName: 'Pipedrive',
    headline: 'ACT! to Pipedrive migration — your sales history, intact.',
    subhead:
      "Pipedrive is built around the deal pipeline; ACT! is built around the contact. Our AI " +
      "remaps your ACT! opportunities into Pipedrive Deals, attaches the right contacts to each " +
      "Deal's Person field, and preserves every note as a proper Pipedrive activity.",
    painPoint:
      "Pipedrive's Person/Organization/Deal split confuses one-shot CSV imports. " +
      "Your ACT! contacts arrive as Persons but the deals they belong to never get linked.",
    ourEdge:
      "We pre-link every ACT! opportunity to its Person and Organization on the way in, " +
      "and our AI matches free-text notes to the right Person — including notes that mention " +
      "decision-makers by name without ever sitting in the ACT! contact list.",
    keywords: ['act to pipedrive migration', 'export act to pipedrive', 'migrate act crm to pipedrive', 'act database pipedrive import'],
    estimatedTime: '6 minutes',
    seoTitle: 'ACT! to Pipedrive Migration — AI Deal Mapping | MigrationCrunch',
    seoDescription: 'Migrate ACT! to Pipedrive in 6 minutes. AI re-links notes to Persons. Deal pipeline preserved. Patent-pending. From $599.',
  },
  'act-to-zoho': {
    slug: 'act-to-zoho',
    sourceId: 'act', sourceName: 'ACT!',
    destId: 'zoho', destName: 'Zoho CRM',
    headline: 'ACT! to Zoho CRM migration — full sales history preserved.',
    subhead:
      "Zoho CRM's Lead/Contact/Account hierarchy is more rigid than ACT!'s flat contact list. " +
      "Our AI splits your ACT! contacts intelligently — unconverted prospects become Leads, " +
      "active customers become Contacts under their Accounts, and your note history follows " +
      "every record.",
    painPoint:
      "Zoho's strict Lead/Contact distinction confuses standard ACT! exports. " +
      "Worse — Zoho silently truncates note text past 32,000 characters per record.",
    ourEdge:
      "We chunk and re-link long ACT! notes across Zoho's character limit, then use our patent-pending " +
      "AI to identify the entities inside each note and create proper Zoho \"Activity\" + \"Notes\" " +
      "links. Nothing gets clipped.",
    keywords: ['act to zoho migration', 'act to zoho crm', 'export act database to zoho', 'migrate sage act to zoho crm'],
    estimatedTime: '8 minutes',
    seoTitle: 'ACT! to Zoho CRM Migration — Notes + Activities | MigrationCrunch',
    seoDescription: 'Migrate ACT! to Zoho CRM with full note + activity history. Lead/Contact/Account split handled by AI. From $599. Patent-pending.',
  },
  'act-to-insightly': {
    slug: 'act-to-insightly',
    sourceId: 'act', sourceName: 'ACT!',
    destId: 'insightly', destName: 'Insightly',
    headline: 'ACT! to Insightly migration — projects + pipeline + notes.',
    subhead:
      "Insightly is one of the few destinations that natively supports the project-management " +
      "side of ACT!'s opportunity tracking. We migrate your contacts AND projects AND notes — " +
      "with cross-references inside notes properly re-linked.",
    painPoint:
      "Most migration tools ignore Insightly's Project objects entirely, dumping ACT! opportunities " +
      "as plain Opportunities and losing the milestone/task structure.",
    ourEdge:
      "Our preset detects multi-step ACT! opportunities and maps them to Insightly Projects with " +
      "the milestones intact. The patent-pending note AI handles the cross-references.",
    keywords: ['act to insightly migration', 'export act to insightly', 'act crm insightly import'],
    estimatedTime: '8 minutes',
    seoTitle: 'ACT! to Insightly Migration — Projects + Notes | MigrationCrunch',
    seoDescription: 'Migrate ACT! to Insightly with projects, opportunities, and AI-linked notes intact. From $599. Patent-pending note re-association.',
  },
  'act-to-copper': {
    slug: 'act-to-copper',
    sourceId: 'act', sourceName: 'ACT!',
    destId: 'copper', destName: 'Copper',
    headline: 'ACT! to Copper migration — clean Google Workspace handoff.',
    subhead:
      "Moving to Copper usually means committing fully to Google Workspace. We migrate ACT! " +
      "contacts to Copper People, map ACT! companies to Copper Companies, and ensure the " +
      "Gmail addresses in your contact records sync cleanly with the Google directory.",
    painPoint:
      "Copper's Gmail-first model rejects ACT! contacts whose primary email isn't deliverable. " +
      "Generic exports either silently skip those rows or fail mid-import.",
    ourEdge:
      "Our pre-flight check flags bad ACT! emails BEFORE the Copper import runs — you fix them " +
      "in one screen, then push clean data through. Note cross-references re-link automatically.",
    keywords: ['act to copper migration', 'act to copper crm', 'export act database to copper', 'sage act copper'],
    estimatedTime: '5 minutes',
    seoTitle: 'ACT! to Copper Migration — Gmail-Ready | MigrationCrunch',
    seoDescription: 'Migrate ACT! to Copper with clean Gmail-validated contacts and AI-linked notes. Pre-flight email check included. From $599.',
  },
  'act-to-capsule': {
    slug: 'act-to-capsule',
    sourceId: 'act', sourceName: 'ACT!',
    destId: 'capsule', destName: 'Capsule CRM',
    headline: 'ACT! to Capsule CRM migration — simple, clean, complete.',
    subhead:
      "Capsule's simpler data model makes it a popular ACT! escape route for small teams. " +
      "We hand you a Capsule-ready CSV with People, Organisations, and Notes properly linked — " +
      "and our AI rebuilds the contact-to-contact references hiding in your ACT! note text.",
    painPoint:
      "Capsule's free tier has a 250-contact cap and won't import duplicates. " +
      "Most ACT! databases hit both limits in their first hour without pre-flight cleanup.",
    ourEdge:
      "Pre-flight identifies your duplicates and gives you a one-click merge. Below 250 contacts " +
      "you can ride Capsule's free tier; above it, our $599 Starter Pro tier still beats Capsule's " +
      "premium upcharge.",
    keywords: ['act to capsule crm migration', 'act to capsule', 'export act to capsule crm'],
    estimatedTime: '5 minutes',
    seoTitle: 'ACT! to Capsule CRM Migration — Dedupe + Notes | MigrationCrunch',
    seoDescription: 'Migrate ACT! to Capsule CRM with duplicate detection and AI-linked notes. Fits the free tier (≤250 contacts). From $599 for larger.',
  },
  'act-to-less-annoying': {
    slug: 'act-to-less-annoying',
    sourceId: 'act', sourceName: 'ACT!',
    destId: 'generic', destName: 'Less Annoying CRM',
    headline: 'ACT! to Less Annoying CRM migration — the gentle landing.',
    subhead:
      "Less Annoying CRM was built for ACT! refugees who want simpler software without losing " +
      "their note history. Our AI exports your ACT! contacts, companies, and historical notes " +
      "into LACRM's expected format — including the cross-references inside the notes.",
    painPoint:
      "LACRM's flat structure means lost note context if you use a generic CSV — every ACT! " +
      "note becomes a wall of text with no link to the people it mentions.",
    ourEdge:
      "We tag each LACRM contact with structured note metadata so search actually works. " +
      "\"Find all notes mentioning Bob Williams\" returns results the first day.",
    keywords: ['act to less annoying crm migration', 'act to lacrm', 'migrate act to less annoying'],
    estimatedTime: '5 minutes',
    seoTitle: 'ACT! to Less Annoying CRM Migration — AI Notes | MigrationCrunch',
    seoDescription: 'Migrate ACT! to Less Annoying CRM with searchable note history. Patent-pending AI links contact references. From $599.',
  },
  'act-to-freshsales': {
    slug: 'act-to-freshsales',
    sourceId: 'act', sourceName: 'ACT!',
    destId: 'freshsales', destName: 'Freshsales',
    headline: 'ACT! to Freshsales migration — full sales workflow preserved.',
    subhead:
      "Freshsales (Freshworks CRM) brings deal pipelines, AI scoring, and built-in phone/email " +
      "to ACT! refugees. We migrate your contacts, deals, and notes — and our patent-pending " +
      "note AI ensures Freshsales' lead scoring can actually read the relationships in your history.",
    painPoint:
      "Freshsales' AI lead scoring is only as good as the connected data it sees. " +
      "Note text without linked entities = noise the scorer ignores.",
    ourEdge:
      "We structure your ACT! notes so Freshsales' AI can score them. Day-one lead scoring is " +
      "actually meaningful, not zeros across the board.",
    keywords: ['act to freshsales migration', 'act to freshworks crm', 'export act database to freshsales'],
    estimatedTime: '7 minutes',
    seoTitle: 'ACT! to Freshsales Migration — AI-Ready Notes | MigrationCrunch',
    seoDescription: 'Migrate ACT! to Freshsales / Freshworks CRM with structured notes that feed AI lead scoring. From $599. Patent-pending.',
  },
  'act-to-keap': {
    slug: 'act-to-keap',
    sourceId: 'act', sourceName: 'ACT!',
    destId: 'keap', destName: 'Keap',
    headline: 'ACT! to Keap migration — contacts, automations, and notes.',
    subhead:
      "Keap (formerly Infusionsoft) is a popular upgrade for ACT! users who want marketing " +
      "automation on top of their contact list. We migrate contacts, tag-ready segments, and " +
      "the historical notes that Keap's automation engine needs to trigger correctly.",
    painPoint:
      "Keap's automation triggers fire on contact tags and field values. " +
      "Generic ACT! exports lose the implicit segmentation buried in ACT! groups and Notes.",
    ourEdge:
      "Our AI surfaces the implicit segments in your ACT! notes (\"VIP\", \"past customer\", " +
      "\"referred by X\") and pre-tags Keap contacts so Day 1 automations work like Day 1000.",
    keywords: ['act to keap migration', 'act to infusionsoft migration', 'export act crm to keap'],
    estimatedTime: '8 minutes',
    seoTitle: 'ACT! to Keap Migration — Tags + Automations | MigrationCrunch',
    seoDescription: 'Migrate ACT! to Keap (Infusionsoft) with AI-derived tags for instant automation triggers. From $599. Patent-pending note recovery.',
  },
  'act-to-monday': {
    slug: 'act-to-monday',
    sourceId: 'act', sourceName: 'ACT!',
    destId: 'monday', destName: 'monday.com',
    headline: 'ACT! to monday.com migration — board-ready contacts and pipeline.',
    subhead:
      "monday.com isn't strictly a CRM — it's a flexible board engine. We migrate your ACT! " +
      "contacts, opportunities, and notes into a board structure that monday.com's CRM template " +
      "can ingest directly, with note references properly linked across items.",
    painPoint:
      "monday.com expects rigid columns; ACT! notes are unstructured. Forcing them into a " +
      "single Long Text column loses every searchable name and company.",
    ourEdge:
      "We split each ACT! note into structured monday.com columns — People mentioned, Companies " +
      "referenced, Next action — so monday's filters and automations can act on them.",
    keywords: ['act to monday com migration', 'act crm to monday', 'export act database to monday.com'],
    estimatedTime: '9 minutes',
    seoTitle: 'ACT! to monday.com Migration — Structured Notes | MigrationCrunch',
    seoDescription: 'Migrate ACT! to monday.com with notes split into searchable columns. From $599. Patent-pending AI extraction.',
  },
  'act-to-generic': {
    slug: 'act-to-generic',
    sourceId: 'act', sourceName: 'ACT!',
    destId: 'generic', destName: 'any CRM',
    headline: 'Export ACT! to a clean, AI-enhanced CSV — for any CRM.',
    subhead:
      "Not sure where you're going yet? Get your ACT! database as a clean, deduplicated, " +
      "AI-enhanced CSV with the relationships inside your notes already extracted into " +
      "structured columns. Import it into any CRM later — Salesforce, HubSpot, Pipedrive, " +
      "Zoho, anything.",
    painPoint:
      "Stock ACT! exports produce a CSV that any CRM rejects on first import — duplicates, " +
      "broken emails, missing fields, and notes that lose all their internal references.",
    ourEdge:
      "One clean export → use it 10 times. The structured columns we generate work as drop-in " +
      "fields in literally any modern CRM.",
    keywords: ['export act database to csv', 'act to csv with notes', 'act database export tool', 'act crm export'],
    estimatedTime: '4 minutes',
    seoTitle: 'Export ACT! to Clean CSV (AI-Enhanced) | MigrationCrunch',
    seoDescription: 'Export ACT! as a clean, deduplicated CSV with AI-extracted note relationships. Import into any CRM. From $599 — try free first.',
  },

  // ═══════════════════════════════════════════════════════════════
  //  PIPEDRIVE SOURCE PAIRS (Pipedrive → 11 destinations)
  //  Pipedrive's people-first data model + activity log + custom
  //  fields lose meaning on export. We preserve them.
  // ═══════════════════════════════════════════════════════════════
  'pipedrive-to-salesforce': {
    slug: 'pipedrive-to-salesforce',
    sourceId: 'pipedrive', sourceName: 'Pipedrive',
    destId: 'salesforce', destName: 'Salesforce',
    headline: 'Pipedrive to Salesforce migration — every deal, every activity, every note.',
    subhead:
      "Outgrowing Pipedrive? Salesforce's data model is richer but unforgiving on import. " +
      "Our AI maps Pipedrive Persons to Salesforce Contacts, Organizations to Accounts, " +
      "and rebuilds the cross-references hidden in your activity history.",
    painPoint:
      "Pipedrive's custom fields and pipeline stages don't have direct Salesforce equivalents — " +
      "generic exports flatten them into a giant Description blob.",
    ourEdge:
      "We map Pipedrive custom fields to Salesforce custom fields one-for-one and re-link " +
      "the contact references inside your activity notes to the right Salesforce records.",
    keywords: ['pipedrive to salesforce migration', 'pipedrive salesforce import', 'migrate pipedrive to salesforce'],
    estimatedTime: '9 minutes',
    seoTitle: 'Pipedrive to Salesforce Migration — Full Activity History | MigrationCrunch',
    seoDescription: 'Migrate Pipedrive to Salesforce with deals, activities, and AI-linked notes intact. Custom fields preserved. From $599.',
  },
  'pipedrive-to-hubspot': {
    slug: 'pipedrive-to-hubspot',
    sourceId: 'pipedrive', sourceName: 'Pipedrive',
    destId: 'hubspot', destName: 'HubSpot',
    headline: 'Pipedrive to HubSpot migration — deals, contacts, and history preserved.',
    subhead:
      "Moving from Pipedrive's deal-centric model to HubSpot's contact-centric one isn't a " +
      "simple CSV swap. Our AI restructures your data so HubSpot Deals carry the right " +
      "Contact + Company associations from day one.",
    painPoint:
      "HubSpot Free has hard limits on custom properties; Pipedrive users routinely have dozens. " +
      "Generic exports either drop them or burn through your HubSpot property quota on noise.",
    ourEdge:
      "We score and prioritize your Pipedrive custom fields by signal value, so the most " +
      "important ones land in HubSpot under your quota. The rest are AI-summarized into notes.",
    keywords: ['pipedrive to hubspot migration', 'pipedrive hubspot import', 'switch from pipedrive to hubspot'],
    estimatedTime: '8 minutes',
    seoTitle: 'Pipedrive to HubSpot Migration — AI-Optimized | MigrationCrunch',
    seoDescription: 'Migrate Pipedrive to HubSpot with deals, activities, and notes intact. Custom field quota optimized by AI. From $599.',
  },
  'pipedrive-to-zoho': {
    slug: 'pipedrive-to-zoho',
    sourceId: 'pipedrive', sourceName: 'Pipedrive',
    destId: 'zoho', destName: 'Zoho CRM',
    headline: 'Pipedrive to Zoho CRM migration — keep the cost down, keep the data clean.',
    subhead:
      "Zoho's lower per-seat cost makes it a popular Pipedrive replacement for growing teams. " +
      "We migrate your Persons, Organizations, Deals, and activity notes — with cross-references " +
      "intact.",
    painPoint:
      "Zoho's stricter Lead/Contact/Account distinction confuses Pipedrive exports that mix " +
      "active customers with cold prospects under the same Person record.",
    ourEdge:
      "Our AI classifies each Pipedrive Person as Lead vs Contact based on deal stage history " +
      "and activity recency — so Zoho's pipeline reports work on day one.",
    keywords: ['pipedrive to zoho migration', 'pipedrive to zoho crm', 'migrate pipedrive to zoho'],
    estimatedTime: '9 minutes',
    seoTitle: 'Pipedrive to Zoho CRM Migration — Lead/Contact Split | MigrationCrunch',
    seoDescription: 'Migrate Pipedrive to Zoho CRM with intelligent Lead vs Contact classification. Notes + activities preserved. From $599.',
  },
  'pipedrive-to-act': {
    slug: 'pipedrive-to-act',
    sourceId: 'pipedrive', sourceName: 'Pipedrive',
    destId: 'generic', destName: 'ACT! / SalesCrunchCRM',
    headline: 'Pipedrive to ACT! / SalesCrunchCRM migration — back to the basics, smarter.',
    subhead:
      "Burned out on Pipedrive's pipeline complexity? SalesCrunchCRM is the modern ACT! — " +
      "contact-centric, simple, and built for the 80% of sales teams that don't need 15 pipeline stages.",
    painPoint:
      "ACT!-style CRMs are flat contact lists; Pipedrive's deal hierarchy doesn't translate. " +
      "Most teams end up with thousands of orphan deal records cluttering the contact view.",
    ourEdge:
      "We merge Pipedrive Deal history INTO the Person record's note timeline — the deal " +
      "story lives where you actually look for it: under the contact.",
    keywords: ['pipedrive to act migration', 'pipedrive to salescrunchcrm', 'leave pipedrive for simpler crm'],
    estimatedTime: '7 minutes',
    seoTitle: 'Pipedrive to ACT! / SalesCrunchCRM Migration | MigrationCrunch',
    seoDescription: 'Migrate from Pipedrive to a simpler contact-first CRM. Deal history merged into the contact timeline. Free to SalesCrunchCRM.',
  },
  'pipedrive-to-monday': {
    slug: 'pipedrive-to-monday',
    sourceId: 'pipedrive', sourceName: 'Pipedrive',
    destId: 'monday', destName: 'monday.com',
    headline: 'Pipedrive to monday.com migration — your CRM data, your board structure.',
    subhead:
      "monday.com's flexible board model lets you customize where Pipedrive's pipeline does not. " +
      "We map your Pipedrive data into monday's CRM template with the right column types.",
    painPoint:
      "Pipedrive activity logs become unstructured text in monday's Long Text column — losing " +
      "all filterability and automation potential.",
    ourEdge:
      "We split activities into monday-native columns: People mentioned, Next action, Status " +
      "tag — so monday's filters and automations work immediately.",
    keywords: ['pipedrive to monday com migration', 'pipedrive to monday', 'migrate pipedrive to monday'],
    estimatedTime: '10 minutes',
    seoTitle: 'Pipedrive to monday.com Migration — Structured | MigrationCrunch',
    seoDescription: 'Migrate Pipedrive to monday.com with activities split into searchable columns. Patent-pending AI. From $599.',
  },
  'pipedrive-to-insightly': {
    slug: 'pipedrive-to-insightly',
    sourceId: 'pipedrive', sourceName: 'Pipedrive',
    destId: 'insightly', destName: 'Insightly',
    headline: 'Pipedrive to Insightly migration — pipeline + project tracking, unified.',
    subhead:
      "Insightly's Project objects fill the gap Pipedrive leaves for post-sale work. " +
      "We migrate your Pipedrive deals as Insightly Opportunities AND auto-spawn Projects " +
      "for closed-won deals, with notes carried across.",
    painPoint:
      "Generic exports drop Pipedrive's deal won-history into a single Opportunity record — " +
      "the post-sale milestones never make it to Insightly's Project tracker.",
    ourEdge:
      "Our AI reads your Pipedrive notes for milestone language (\"shipped\", \"renewed\", " +
      "\"upsold\") and pre-builds Insightly Projects with those as starting tasks.",
    keywords: ['pipedrive to insightly migration', 'pipedrive insightly import'],
    estimatedTime: '9 minutes',
    seoTitle: 'Pipedrive to Insightly Migration — Projects + Pipeline | MigrationCrunch',
    seoDescription: 'Migrate Pipedrive to Insightly with deals AND auto-generated projects for closed-won opportunities. From $599.',
  },
  'pipedrive-to-copper': {
    slug: 'pipedrive-to-copper',
    sourceId: 'pipedrive', sourceName: 'Pipedrive',
    destId: 'copper', destName: 'Copper',
    headline: 'Pipedrive to Copper migration — committed to Google Workspace.',
    subhead:
      "If your team lives in Gmail and Google Workspace, Copper's tight integration is reason " +
      "enough to switch. We migrate Pipedrive contacts, deals, and notes into Copper People + " +
      "Companies + Opportunities with the Gmail-validation pre-flight built in.",
    painPoint:
      "Copper rejects contacts without deliverable email addresses; Pipedrive lets you store " +
      "anything. Imports fail loudly mid-run.",
    ourEdge:
      "Our pre-flight surfaces every Pipedrive contact with an invalid email BEFORE the Copper " +
      "import runs. Fix in one screen, push clean data.",
    keywords: ['pipedrive to copper migration', 'pipedrive to copper crm', 'migrate pipedrive to copper'],
    estimatedTime: '7 minutes',
    seoTitle: 'Pipedrive to Copper Migration — Gmail Validated | MigrationCrunch',
    seoDescription: 'Migrate Pipedrive to Copper with Gmail-validated contacts and AI-linked notes. From $599. Patent-pending.',
  },
  'pipedrive-to-freshsales': {
    slug: 'pipedrive-to-freshsales',
    sourceId: 'pipedrive', sourceName: 'Pipedrive',
    destId: 'freshsales', destName: 'Freshsales',
    headline: 'Pipedrive to Freshsales migration — AI lead scoring, day one.',
    subhead:
      "Freshsales (Freshworks CRM) is Pipedrive's closest one-for-one alternative with AI lead " +
      "scoring baked in. We migrate everything — and structure your Pipedrive notes so " +
      "Freshsales' AI can actually read them.",
    painPoint:
      "Freshsales' AI lead scoring needs structured signals. Pipedrive notes arrive as text " +
      "blobs that the scorer treats as noise.",
    ourEdge:
      "We extract decision-maker mentions, competitor mentions, and stage-keywords from your " +
      "Pipedrive notes — Freshsales' scoring sees them as proper signals.",
    keywords: ['pipedrive to freshsales migration', 'pipedrive to freshworks crm'],
    estimatedTime: '8 minutes',
    seoTitle: 'Pipedrive to Freshsales Migration — AI-Ready | MigrationCrunch',
    seoDescription: 'Migrate Pipedrive to Freshsales / Freshworks CRM with structured notes for AI lead scoring. From $599.',
  },
  'pipedrive-to-keap': {
    slug: 'pipedrive-to-keap',
    sourceId: 'pipedrive', sourceName: 'Pipedrive',
    destId: 'keap', destName: 'Keap',
    headline: 'Pipedrive to Keap migration — sales pipeline + marketing automation.',
    subhead:
      "Keap (Infusionsoft) layers email marketing and automation on top of contact management. " +
      "We migrate your Pipedrive data — and pre-tag contacts based on their deal stage history " +
      "so Keap's automations can fire from day one.",
    painPoint:
      "Keap's automation needs contact tags to trigger. Pipedrive exports lose deal-stage context.",
    ourEdge:
      "We convert Pipedrive deal stages to Keap tags automatically — \"Stage: Proposal\" becomes " +
      "tag \"proposal-sent\" so your nurture campaign starts immediately.",
    keywords: ['pipedrive to keap migration', 'pipedrive to infusionsoft', 'migrate pipedrive to keap'],
    estimatedTime: '9 minutes',
    seoTitle: 'Pipedrive to Keap Migration — Tags + Automations | MigrationCrunch',
    seoDescription: 'Migrate Pipedrive to Keap with deal stages converted to Keap tags for instant automation. From $599.',
  },
  'pipedrive-to-capsule': {
    slug: 'pipedrive-to-capsule',
    sourceId: 'pipedrive', sourceName: 'Pipedrive',
    destId: 'capsule', destName: 'Capsule CRM',
    headline: 'Pipedrive to Capsule CRM migration — simpler, cheaper, complete.',
    subhead:
      "Capsule's simpler data model + lower price point makes it a popular Pipedrive escape. " +
      "We map Persons → People, Organizations → Organisations, and consolidate Deals + Activities " +
      "into Capsule's Opportunity model.",
    painPoint:
      "Capsule's free tier caps at 250 contacts; most Pipedrive databases blow past that.",
    ourEdge:
      "Our pre-flight ranks your Pipedrive contacts by recent activity so if you do need to " +
      "trim, you keep the most active 250. Everyone else exports as a clean spreadsheet backup.",
    keywords: ['pipedrive to capsule crm migration', 'pipedrive to capsule'],
    estimatedTime: '7 minutes',
    seoTitle: 'Pipedrive to Capsule CRM Migration — Activity-Ranked | MigrationCrunch',
    seoDescription: 'Migrate Pipedrive to Capsule CRM with AI-ranked contacts to fit any tier cap. From $599.',
  },
  'pipedrive-to-less-annoying': {
    slug: 'pipedrive-to-less-annoying',
    sourceId: 'pipedrive', sourceName: 'Pipedrive',
    destId: 'generic', destName: 'Less Annoying CRM',
    headline: 'Pipedrive to Less Annoying CRM migration — back to simple, on purpose.',
    subhead:
      "Less Annoying CRM was built for teams who want CRM to disappear into the background. " +
      "We migrate Pipedrive data into LACRM's flat structure with the search-relevant entities " +
      "from your notes pre-tagged.",
    painPoint:
      "LACRM's flat model means lost note context — Pipedrive's structured activities become " +
      "wall-of-text that breaks search.",
    ourEdge:
      "We extract names, companies, and deal references from your Pipedrive notes and add " +
      "them as searchable LACRM contact tags.",
    keywords: ['pipedrive to less annoying crm migration', 'pipedrive to lacrm'],
    estimatedTime: '6 minutes',
    seoTitle: 'Pipedrive to Less Annoying CRM Migration | MigrationCrunch',
    seoDescription: 'Migrate Pipedrive to Less Annoying CRM with searchable AI-tagged contacts. From $599. Patent-pending.',
  },

  // ═══════════════════════════════════════════════════════════════
  //  SALESFORCE SOURCE PAIRS (Salesforce → 11 destinations)
  //  SF defectors are usually downscaling — they overbought.
  // ═══════════════════════════════════════════════════════════════
  'salesforce-to-hubspot': {
    slug: 'salesforce-to-hubspot',
    sourceId: 'salesforce', sourceName: 'Salesforce',
    destId: 'hubspot', destName: 'HubSpot',
    headline: 'Salesforce to HubSpot migration — cleaner data, lower cost.',
    subhead:
      "Most Salesforce defectors paid for capabilities they never used. HubSpot's tiered " +
      "pricing fits real-world sales teams better. We migrate Leads, Contacts, Accounts, " +
      "Opportunities, and Activities — with the meaningful relationships inside notes preserved.",
    painPoint:
      "Salesforce's complex object model (Leads vs Contacts vs Person Accounts) doesn't " +
      "translate directly to HubSpot. Generic tools duplicate or drop records.",
    ourEdge:
      "Our AI consolidates Salesforce Person Accounts + Leads + Contacts into a single clean " +
      "HubSpot Contact record with the relationship history intact.",
    keywords: ['salesforce to hubspot migration', 'switch from salesforce to hubspot', 'migrate salesforce to hubspot'],
    estimatedTime: '12 minutes',
    seoTitle: 'Salesforce to HubSpot Migration — AI Consolidation | MigrationCrunch',
    seoDescription: 'Migrate Salesforce to HubSpot. Person Accounts + Leads + Contacts merged intelligently. AI-linked notes. From $599.',
  },
  'salesforce-to-pipedrive': {
    slug: 'salesforce-to-pipedrive',
    sourceId: 'salesforce', sourceName: 'Salesforce',
    destId: 'pipedrive', destName: 'Pipedrive',
    headline: 'Salesforce to Pipedrive migration — your pipeline, simplified.',
    subhead:
      "Pipedrive's deal-first interface is how Salesforce should have always worked for sales " +
      "teams. We migrate your Opportunities to Deals, Accounts to Organizations, and Contacts " +
      "to Persons — with every activity properly linked.",
    painPoint:
      "Salesforce custom objects don't exist in Pipedrive. Generic exports either drop them " +
      "or flatten them into giant Notes fields that no one reads.",
    ourEdge:
      "We summarize your Salesforce custom object data into Pipedrive custom fields where " +
      "they fit, and preserve the full record in archived notes for reference.",
    keywords: ['salesforce to pipedrive migration', 'switch from salesforce to pipedrive', 'sf to pipedrive'],
    estimatedTime: '11 minutes',
    seoTitle: 'Salesforce to Pipedrive Migration — Pipeline-First | MigrationCrunch',
    seoDescription: 'Migrate Salesforce to Pipedrive with Opportunities → Deals, custom objects intelligently mapped. From $599.',
  },
  'salesforce-to-zoho': {
    slug: 'salesforce-to-zoho',
    sourceId: 'salesforce', sourceName: 'Salesforce',
    destId: 'zoho', destName: 'Zoho CRM',
    headline: 'Salesforce to Zoho CRM migration — same power, fraction of the cost.',
    subhead:
      "Zoho CRM offers Salesforce-class functionality at a quarter of the per-seat cost. " +
      "Our AI maps the entire Salesforce schema to Zoho's equivalent objects, including " +
      "custom fields and relationships.",
    painPoint:
      "Zoho has a 32k char limit per Notes field; Salesforce Description fields routinely " +
      "exceed it. Naïve imports truncate silently.",
    ourEdge:
      "We chunk long Salesforce notes across multiple Zoho Activity records, preserving " +
      "chronological order and inter-note references via our patent-pending AI.",
    keywords: ['salesforce to zoho migration', 'salesforce to zoho crm', 'migrate sf to zoho'],
    estimatedTime: '13 minutes',
    seoTitle: 'Salesforce to Zoho CRM Migration — Same Power | MigrationCrunch',
    seoDescription: 'Migrate Salesforce to Zoho CRM with full custom-field mapping and chunked notes. From $599. Save 75% per seat.',
  },
  'salesforce-to-act': {
    slug: 'salesforce-to-act',
    sourceId: 'salesforce', sourceName: 'Salesforce',
    destId: 'generic', destName: 'ACT! / SalesCrunchCRM',
    headline: 'Salesforce to ACT! / SalesCrunchCRM — the radical downsize.',
    subhead:
      "Some teams realize Salesforce is killing them. SalesCrunchCRM is the modern ACT! — " +
      "contact-centric, blazingly fast, no admin certifications required. We migrate the " +
      "Salesforce data that actually matters and leave the bloat behind.",
    painPoint:
      "Salesforce's hundreds of standard + custom fields don't all matter. Generic exports " +
      "carry every single one into the new CRM, recreating the original problem.",
    ourEdge:
      "Our AI scores Salesforce fields by actual usage in the past 12 months and only " +
      "migrates the ones that have data. The rest get archived as a CSV reference.",
    keywords: ['salesforce to act migration', 'leave salesforce', 'salesforce alternative ai migration'],
    estimatedTime: '10 minutes',
    seoTitle: 'Salesforce to ACT! / SalesCrunchCRM Migration | MigrationCrunch',
    seoDescription: 'Migrate Salesforce to SalesCrunchCRM. AI keeps only the data you actually use. Free to SalesCrunchCRM.',
  },
  'salesforce-to-insightly': {
    slug: 'salesforce-to-insightly',
    sourceId: 'salesforce', sourceName: 'Salesforce',
    destId: 'insightly', destName: 'Insightly',
    headline: 'Salesforce to Insightly migration — sales + projects in one place.',
    subhead:
      "Insightly bundles CRM and project management — useful for service businesses that " +
      "bought Salesforce + a separate PM tool. We migrate Salesforce sales data AND any " +
      "linked project context buried in opportunity notes.",
    painPoint:
      "Insightly's Project model has no Salesforce equivalent — opportunity notes that " +
      "reference project milestones get lost in translation.",
    ourEdge:
      "Our AI detects milestone language in Salesforce notes and pre-builds Insightly Projects " +
      "with those milestones as tasks.",
    keywords: ['salesforce to insightly migration', 'sf to insightly'],
    estimatedTime: '10 minutes',
    seoTitle: 'Salesforce to Insightly Migration — Projects Included | MigrationCrunch',
    seoDescription: 'Migrate Salesforce to Insightly with AI-generated Project milestones from your notes. From $599.',
  },
  'salesforce-to-copper': {
    slug: 'salesforce-to-copper',
    sourceId: 'salesforce', sourceName: 'Salesforce',
    destId: 'copper', destName: 'Copper',
    headline: 'Salesforce to Copper migration — for Google Workspace shops.',
    subhead:
      "Copper's Gmail integration replaces Salesforce's email-tracking with something that " +
      "actually works. We migrate People, Companies, Opportunities, and Activities — " +
      "validating emails against the Google directory along the way.",
    painPoint:
      "Salesforce's email-tracking creates contact records from every random sender. Copper " +
      "rejects them en masse.",
    ourEdge:
      "Pre-flight identifies Salesforce contacts with no real engagement history and " +
      "archives them, so only meaningful contacts reach Copper.",
    keywords: ['salesforce to copper migration', 'sf to copper crm'],
    estimatedTime: '9 minutes',
    seoTitle: 'Salesforce to Copper Migration — Gmail-First | MigrationCrunch',
    seoDescription: 'Migrate Salesforce to Copper with AI-validated emails and clean engagement history. From $599.',
  },
  'salesforce-to-freshsales': {
    slug: 'salesforce-to-freshsales',
    sourceId: 'salesforce', sourceName: 'Salesforce',
    destId: 'freshsales', destName: 'Freshsales',
    headline: 'Salesforce to Freshsales migration — same features, simpler license.',
    subhead:
      "Freshsales bundles email, phone, and AI scoring at a fixed price — no per-feature " +
      "Salesforce nickel-and-diming. We migrate the full Salesforce data model, with notes " +
      "structured for Freshsales' AI scoring.",
    painPoint:
      "Salesforce's Activity history is the richest dataset in the entire org. Generic " +
      "exports lose 80% of it during CSV conversion.",
    ourEdge:
      "We preserve every Salesforce Activity as a Freshsales Activity record — call, email, " +
      "and task records each in their proper place, with cross-references intact.",
    keywords: ['salesforce to freshsales migration', 'salesforce to freshworks crm'],
    estimatedTime: '11 minutes',
    seoTitle: 'Salesforce to Freshsales Migration — Full Activity History | MigrationCrunch',
    seoDescription: 'Migrate Salesforce to Freshsales / Freshworks CRM with complete activity history preserved. From $599.',
  },
  'salesforce-to-keap': {
    slug: 'salesforce-to-keap',
    sourceId: 'salesforce', sourceName: 'Salesforce',
    destId: 'keap', destName: 'Keap',
    headline: 'Salesforce to Keap migration — sales + marketing reunited.',
    subhead:
      "Keap (Infusionsoft) replaces both Salesforce Sales Cloud AND Pardot/Marketing Cloud for " +
      "small businesses. We migrate Leads + Contacts + Opportunities + Campaign Membership and " +
      "rebuild them as Keap contacts with proper automation tags.",
    painPoint:
      "Salesforce Campaign Membership data is invaluable for Keap's automation engine but " +
      "evaporates in generic CSV exports.",
    ourEdge:
      "We preserve every Salesforce Campaign Membership as a Keap tag, so your Day 1 " +
      "automations have full lifecycle awareness.",
    keywords: ['salesforce to keap migration', 'salesforce to infusionsoft', 'sf to keap'],
    estimatedTime: '11 minutes',
    seoTitle: 'Salesforce to Keap Migration — Campaign Data Preserved | MigrationCrunch',
    seoDescription: 'Migrate Salesforce to Keap with Campaign Membership converted to automation tags. From $599.',
  },
  'salesforce-to-capsule': {
    slug: 'salesforce-to-capsule',
    sourceId: 'salesforce', sourceName: 'Salesforce',
    destId: 'capsule', destName: 'Capsule CRM',
    headline: 'Salesforce to Capsule CRM migration — radical simplification.',
    subhead:
      "Capsule is the polar opposite of Salesforce — clean, focused, fast. For teams who want " +
      "to stop fighting their CRM. We migrate the data that matters and leave the rest.",
    painPoint:
      "Capsule doesn't try to replicate Salesforce's depth. Most teams panic about \"losing\" " +
      "features they never used.",
    ourEdge:
      "Our AI audit report shows you EXACTLY what Salesforce data you're leaving behind " +
      "(usually 0% of what you actually used). Then it migrates the rest cleanly.",
    keywords: ['salesforce to capsule crm migration', 'sf to capsule'],
    estimatedTime: '9 minutes',
    seoTitle: 'Salesforce to Capsule CRM Migration — Audit-Driven | MigrationCrunch',
    seoDescription: 'Migrate Salesforce to Capsule CRM. AI audit shows what you actually use vs leave behind. From $599.',
  },
  'salesforce-to-monday': {
    slug: 'salesforce-to-monday',
    sourceId: 'salesforce', sourceName: 'Salesforce',
    destId: 'monday', destName: 'monday.com',
    headline: 'Salesforce to monday.com migration — your CRM, your structure.',
    subhead:
      "monday.com's flexibility appeals to teams burned by Salesforce's rigidity. We migrate " +
      "your Salesforce objects into monday boards with the right column types and view config.",
    painPoint:
      "Salesforce's reporting features don't translate to monday automatically. Teams " +
      "lose dashboard visibility during the move.",
    ourEdge:
      "We auto-generate the most common monday dashboards (pipeline by stage, deals by owner, " +
      "activity by week) from your Salesforce reporting data.",
    keywords: ['salesforce to monday com migration', 'sf to monday'],
    estimatedTime: '12 minutes',
    seoTitle: 'Salesforce to monday.com Migration — Dashboards Included | MigrationCrunch',
    seoDescription: 'Migrate Salesforce to monday.com with auto-generated dashboards. AI-linked notes. From $599.',
  },
  'salesforce-to-less-annoying': {
    slug: 'salesforce-to-less-annoying',
    sourceId: 'salesforce', sourceName: 'Salesforce',
    destId: 'generic', destName: 'Less Annoying CRM',
    headline: 'Salesforce to Less Annoying CRM migration — the deep breath.',
    subhead:
      "Less Annoying CRM is what Salesforce founders originally pitched in 1999 before scope " +
      "creep. For teams who want CRM to feel like the email-and-calendar tool it should be.",
    painPoint:
      "LACRM's simplicity makes ex-Salesforce admins twitchy. Without structured note search, " +
      "they can't find anything.",
    ourEdge:
      "Our AI extracts every name, company, and deal reference from your Salesforce notes " +
      "into LACRM-searchable tags. Your old data feels findable again on day one.",
    keywords: ['salesforce to less annoying crm migration', 'sf to lacrm', 'simple salesforce alternative'],
    estimatedTime: '8 minutes',
    seoTitle: 'Salesforce to Less Annoying CRM Migration | MigrationCrunch',
    seoDescription: 'Migrate Salesforce to Less Annoying CRM with AI-tagged search-ready contacts. From $599.',
  },

  // ═══════════════════════════════════════════════════════════════
  //  HUBSPOT SOURCE PAIRS (HubSpot → 11 destinations)
  //  HubSpot defectors usually scale UP (Salesforce) or simplify.
  // ═══════════════════════════════════════════════════════════════
  'hubspot-to-salesforce': {
    slug: 'hubspot-to-salesforce',
    sourceId: 'hubspot', sourceName: 'HubSpot',
    destId: 'salesforce', destName: 'Salesforce',
    headline: 'HubSpot to Salesforce migration — scaling up without losing history.',
    subhead:
      "Growing past HubSpot Sales Hub Pro? Salesforce's depth is the next step. We migrate " +
      "Contacts, Companies, Deals, and Engagements — properly mapped to Salesforce's Lead/" +
      "Contact distinction and full Activity model.",
    painPoint:
      "HubSpot's Engagements (notes, calls, emails, meetings) all share one timeline. " +
      "Salesforce splits them across 4 object types. Generic tools dump everything as Tasks.",
    ourEdge:
      "Our AI classifies each HubSpot Engagement into its proper Salesforce type (Call, " +
      "Email, Task, Event) based on content analysis — preserving the reporting taxonomy.",
    keywords: ['hubspot to salesforce migration', 'switch from hubspot to salesforce', 'hubspot sf'],
    estimatedTime: '12 minutes',
    seoTitle: 'HubSpot to Salesforce Migration — AI-Classified Activities | MigrationCrunch',
    seoDescription: 'Migrate HubSpot to Salesforce with Engagements correctly classified as Calls / Emails / Tasks. From $599.',
  },
  'hubspot-to-pipedrive': {
    slug: 'hubspot-to-pipedrive',
    sourceId: 'hubspot', sourceName: 'HubSpot',
    destId: 'pipedrive', destName: 'Pipedrive',
    headline: 'HubSpot to Pipedrive migration — sales-first, marketing-light.',
    subhead:
      "If you're a pure-sales team using HubSpot's free CRM, Pipedrive's deal-centric model " +
      "is a more natural fit. We migrate Contacts → Persons, Companies → Organizations, " +
      "Deals → Deals with full activity history.",
    painPoint:
      "HubSpot's contact properties don't map 1:1 to Pipedrive's custom fields — and " +
      "Pipedrive's quota is tighter on free tier.",
    ourEdge:
      "Our AI prioritizes HubSpot contact properties by data density and signal value so " +
      "the top fields land in Pipedrive without burning your quota.",
    keywords: ['hubspot to pipedrive migration', 'leave hubspot for pipedrive'],
    estimatedTime: '9 minutes',
    seoTitle: 'HubSpot to Pipedrive Migration — Sales-First | MigrationCrunch',
    seoDescription: 'Migrate HubSpot to Pipedrive with AI-prioritized custom fields and full deal history. From $599.',
  },
  'hubspot-to-zoho': {
    slug: 'hubspot-to-zoho',
    sourceId: 'hubspot', sourceName: 'HubSpot',
    destId: 'zoho', destName: 'Zoho CRM',
    headline: 'HubSpot to Zoho CRM migration — same features, lower price.',
    subhead:
      "Zoho CRM offers HubSpot Pro features at HubSpot Free pricing. We migrate Contacts, " +
      "Companies, Deals, and Engagements into Zoho's equivalent objects.",
    painPoint:
      "Zoho's strict Lead/Contact/Account hierarchy doesn't match HubSpot's flat Contact + " +
      "Company model. Generic imports duplicate records.",
    ourEdge:
      "Our AI classifies each HubSpot Contact as Zoho Lead (unconverted) vs Contact (active) " +
      "based on lifecycle stage and engagement recency.",
    keywords: ['hubspot to zoho migration', 'hubspot to zoho crm'],
    estimatedTime: '10 minutes',
    seoTitle: 'HubSpot to Zoho CRM Migration — Lead/Contact Split | MigrationCrunch',
    seoDescription: 'Migrate HubSpot to Zoho CRM with AI lifecycle classification. Engagements preserved. From $599.',
  },
  'hubspot-to-act': {
    slug: 'hubspot-to-act',
    sourceId: 'hubspot', sourceName: 'HubSpot',
    destId: 'generic', destName: 'ACT! / SalesCrunchCRM',
    headline: 'HubSpot to ACT! / SalesCrunchCRM — quiet, fast, contact-first.',
    subhead:
      "Burned out on HubSpot's notification firehose? SalesCrunchCRM is contact-first and " +
      "doesn't try to be a marketing platform. We migrate Contacts + Companies + Engagement " +
      "history into a clean SalesCrunchCRM database.",
    painPoint:
      "HubSpot's marketing-flavored data (form submissions, email opens, page views) clutters " +
      "the contact view in any other CRM.",
    ourEdge:
      "Our AI audit identifies and archives marketing-only data, leaving you with a clean " +
      "sales-focused database — same contacts, none of the noise.",
    keywords: ['hubspot to act migration', 'hubspot to salescrunchcrm', 'leave hubspot simpler crm'],
    estimatedTime: '8 minutes',
    seoTitle: 'HubSpot to ACT! / SalesCrunchCRM Migration | MigrationCrunch',
    seoDescription: 'Migrate HubSpot to SalesCrunchCRM with marketing noise filtered out. Free to SalesCrunchCRM.',
  },
  'hubspot-to-insightly': {
    slug: 'hubspot-to-insightly',
    sourceId: 'hubspot', sourceName: 'HubSpot',
    destId: 'insightly', destName: 'Insightly',
    headline: 'HubSpot to Insightly migration — CRM + projects unified.',
    subhead:
      "Service businesses on HubSpot often run a separate PM tool. Insightly merges them. " +
      "We migrate HubSpot data AND auto-build Projects for your closed-won deals.",
    painPoint:
      "HubSpot's deal-stage data doesn't carry forward as project context — your delivery " +
      "team loses the sales-call backstory.",
    ourEdge:
      "Our AI summarizes each HubSpot deal's communication history into a 1-page Project " +
      "brief that lands in Insightly with the contact still linked.",
    keywords: ['hubspot to insightly migration', 'hubspot insightly'],
    estimatedTime: '9 minutes',
    seoTitle: 'HubSpot to Insightly Migration — Projects + Briefs | MigrationCrunch',
    seoDescription: 'Migrate HubSpot to Insightly with AI-generated project briefs for closed-won deals. From $599.',
  },
  'hubspot-to-copper': {
    slug: 'hubspot-to-copper',
    sourceId: 'hubspot', sourceName: 'HubSpot',
    destId: 'copper', destName: 'Copper',
    headline: 'HubSpot to Copper migration — committing to Gmail.',
    subhead:
      "Copper's Gmail-first model is what HubSpot Sales Hub keeps trying to be. If your team " +
      "lives in Gmail, the switch is overdue. We migrate Contacts, Companies, and Engagements " +
      "with Gmail-validated emails.",
    painPoint:
      "HubSpot logs every email exchange as a Contact record. Copper rejects bounce-prone " +
      "imports.",
    ourEdge:
      "Pre-flight validates every HubSpot contact email against deliverability rules — clean " +
      "data goes to Copper, dirty data gets archived for you to review.",
    keywords: ['hubspot to copper migration', 'hubspot to copper crm'],
    estimatedTime: '8 minutes',
    seoTitle: 'HubSpot to Copper Migration — Gmail-Validated | MigrationCrunch',
    seoDescription: 'Migrate HubSpot to Copper with deliverability-validated emails and AI-linked notes. From $599.',
  },
  'hubspot-to-freshsales': {
    slug: 'hubspot-to-freshsales',
    sourceId: 'hubspot', sourceName: 'HubSpot',
    destId: 'freshsales', destName: 'Freshsales',
    headline: 'HubSpot to Freshsales migration — bundled features, predictable pricing.',
    subhead:
      "Freshsales rolls email + phone + AI scoring + workflow automation into one license — " +
      "no HubSpot add-on shopping. We migrate Contacts, Deals, and Engagement history with " +
      "structured signals for Freshsales' AI scoring.",
    painPoint:
      "HubSpot's lead score lives in their own AI model. It doesn't export. Freshsales " +
      "scoring starts from zero unless you give it structured data.",
    ourEdge:
      "We extract signal-rich entities from HubSpot Engagement text — decision-maker mentions, " +
      "competitor mentions, intent words — and surface them as Freshsales scoring inputs.",
    keywords: ['hubspot to freshsales migration', 'hubspot to freshworks crm'],
    estimatedTime: '9 minutes',
    seoTitle: 'HubSpot to Freshsales Migration — AI Scoring | MigrationCrunch',
    seoDescription: 'Migrate HubSpot to Freshsales / Freshworks CRM with AI-ready signal extraction. From $599.',
  },
  'hubspot-to-keap': {
    slug: 'hubspot-to-keap',
    sourceId: 'hubspot', sourceName: 'HubSpot',
    destId: 'keap', destName: 'Keap',
    headline: 'HubSpot to Keap migration — keep the automation, simplify the platform.',
    subhead:
      "Keap (Infusionsoft) is HubSpot Marketing Hub for small businesses, without the " +
      "enterprise tax. We migrate Contacts, Lists, Workflows-as-tags, and Email engagement.",
    painPoint:
      "HubSpot Workflows don't export. The teams that depend on them lose months of " +
      "automation logic when they switch.",
    ourEdge:
      "Our AI reads your HubSpot Workflow JSON exports and translates them into Keap " +
      "campaign blueprints — most fire correctly without modification.",
    keywords: ['hubspot to keap migration', 'hubspot to infusionsoft'],
    estimatedTime: '11 minutes',
    seoTitle: 'HubSpot to Keap Migration — Workflows Translated | MigrationCrunch',
    seoDescription: 'Migrate HubSpot to Keap with AI-translated Workflows → Keap campaigns. From $599.',
  },
  'hubspot-to-capsule': {
    slug: 'hubspot-to-capsule',
    sourceId: 'hubspot', sourceName: 'HubSpot',
    destId: 'capsule', destName: 'Capsule CRM',
    headline: 'HubSpot to Capsule CRM migration — calmer, cheaper, complete.',
    subhead:
      "Capsule is what HubSpot Free should be — focused, fast, no upsell. We migrate the " +
      "data you actually use and skip the marketing exhaust.",
    painPoint:
      "HubSpot's contact view shows 50 properties; Capsule shows the 10 that matter. " +
      "Picking which 10 is a 2-day project.",
    ourEdge:
      "Our AI audit ranks HubSpot properties by actual usage and picks your top 10 " +
      "automatically. The rest archive cleanly.",
    keywords: ['hubspot to capsule crm migration', 'hubspot to capsule'],
    estimatedTime: '7 minutes',
    seoTitle: 'HubSpot to Capsule CRM Migration — Usage-Ranked | MigrationCrunch',
    seoDescription: 'Migrate HubSpot to Capsule with AI-selected top properties. From $599.',
  },
  'hubspot-to-monday': {
    slug: 'hubspot-to-monday',
    sourceId: 'hubspot', sourceName: 'HubSpot',
    destId: 'monday', destName: 'monday.com',
    headline: 'HubSpot to monday.com migration — your data, your dashboards.',
    subhead:
      "monday.com lets you bend the CRM to your process instead of the other way around. " +
      "We migrate HubSpot data into monday boards with the views and automations to match.",
    painPoint:
      "HubSpot's deal pipeline view is opinionated. Teams who customized it lose the layout.",
    ourEdge:
      "We rebuild your HubSpot deal pipeline as a monday board with the same stages, " +
      "owners, and grouping — plus monday's automation hooks pre-wired.",
    keywords: ['hubspot to monday com migration', 'hubspot to monday'],
    estimatedTime: '10 minutes',
    seoTitle: 'HubSpot to monday.com Migration — Pipeline Preserved | MigrationCrunch',
    seoDescription: 'Migrate HubSpot to monday.com with pipeline stages and automations rebuilt. From $599.',
  },
  'hubspot-to-less-annoying': {
    slug: 'hubspot-to-less-annoying',
    sourceId: 'hubspot', sourceName: 'HubSpot',
    destId: 'generic', destName: 'Less Annoying CRM',
    headline: 'HubSpot to Less Annoying CRM migration — the calmer choice.',
    subhead:
      "LACRM is what HubSpot would be if every product manager left the company. We migrate " +
      "the data you actually look at, structured for instant searchability.",
    painPoint:
      "HubSpot's note timeline mixes marketing and sales data. LACRM's flat structure " +
      "would make finding things impossible without help.",
    ourEdge:
      "Our AI tags each HubSpot note with the relevant people, companies, and deal " +
      "references — LACRM's search returns answers, not walls of text.",
    keywords: ['hubspot to less annoying crm migration', 'hubspot to lacrm'],
    estimatedTime: '7 minutes',
    seoTitle: 'HubSpot to Less Annoying CRM Migration | MigrationCrunch',
    seoDescription: 'Migrate HubSpot to Less Annoying CRM with AI-tagged searchable notes. From $599.',
  },

  // ═══════════════════════════════════════════════════════════════
  //  ZOHO SOURCE PAIRS (Zoho → 11 destinations)
  //  Zoho defectors often hit feature ceilings or want simpler.
  // ═══════════════════════════════════════════════════════════════
  'zoho-to-salesforce': {
    slug: 'zoho-to-salesforce',
    sourceId: 'zoho', sourceName: 'Zoho CRM',
    destId: 'salesforce', destName: 'Salesforce',
    headline: 'Zoho CRM to Salesforce migration — the enterprise upgrade.',
    subhead:
      "Outgrowing Zoho? Salesforce's enterprise depth is the natural next step. We migrate " +
      "Leads, Contacts, Accounts, Deals, and Activities — with Zoho's note-truncation " +
      "history (the chunked overflow records) reassembled into complete Salesforce notes.",
    painPoint:
      "Zoho silently truncates notes at 32k chars and stores overflow in cryptic continuation " +
      "records. Generic tools never reassemble them.",
    ourEdge:
      "Our patent-pending AI detects Zoho's note continuation pattern and stitches the chunks " +
      "back into single coherent Salesforce notes — full context restored.",
    keywords: ['zoho to salesforce migration', 'zoho crm to salesforce', 'switch zoho to salesforce'],
    estimatedTime: '11 minutes',
    seoTitle: 'Zoho CRM to Salesforce Migration — Notes Restored | MigrationCrunch',
    seoDescription: 'Migrate Zoho CRM to Salesforce. AI reassembles truncated Zoho notes back to full text. From $599.',
  },
  'zoho-to-hubspot': {
    slug: 'zoho-to-hubspot',
    sourceId: 'zoho', sourceName: 'Zoho CRM',
    destId: 'hubspot', destName: 'HubSpot',
    headline: 'Zoho CRM to HubSpot migration — better marketing automation included.',
    subhead:
      "HubSpot's marketing-CRM unity beats Zoho One's federated approach for most growth " +
      "teams. We migrate Leads + Contacts + Accounts + Deals + Activities — and reassemble " +
      "Zoho's truncated notes into complete HubSpot Engagement records.",
    painPoint:
      "Zoho's Lead/Contact split doesn't map to HubSpot's flat Contact model. Generic " +
      "imports either duplicate or lose conversion history.",
    ourEdge:
      "Our AI merges Zoho Leads + their Converted Contact records into a single HubSpot " +
      "Contact with the full conversion timeline intact.",
    keywords: ['zoho to hubspot migration', 'zoho crm to hubspot'],
    estimatedTime: '10 minutes',
    seoTitle: 'Zoho CRM to HubSpot Migration — Lead Conversion Preserved | MigrationCrunch',
    seoDescription: 'Migrate Zoho CRM to HubSpot. Lead → Contact conversions merged. Notes reassembled. From $599.',
  },
  'zoho-to-pipedrive': {
    slug: 'zoho-to-pipedrive',
    sourceId: 'zoho', sourceName: 'Zoho CRM',
    destId: 'pipedrive', destName: 'Pipedrive',
    headline: 'Zoho CRM to Pipedrive migration — pure sales focus.',
    subhead:
      "Pipedrive's deal-first interface is what pure sales teams want. We migrate Zoho " +
      "Contacts → Persons, Accounts → Organizations, Deals → Deals, and Activities — " +
      "with our patent-pending Zoho-note-reassembly running automatically.",
    painPoint:
      "Zoho's custom field count routinely exceeds Pipedrive's plan limits.",
    ourEdge:
      "Our AI ranks Zoho custom fields by data density. The top fields land in Pipedrive; " +
      "the long tail is summarized into Pipedrive Person notes.",
    keywords: ['zoho to pipedrive migration', 'zoho crm to pipedrive'],
    estimatedTime: '9 minutes',
    seoTitle: 'Zoho CRM to Pipedrive Migration — Field Optimized | MigrationCrunch',
    seoDescription: 'Migrate Zoho CRM to Pipedrive with AI-ranked custom fields and reassembled notes. From $599.',
  },
  'zoho-to-act': {
    slug: 'zoho-to-act',
    sourceId: 'zoho', sourceName: 'Zoho CRM',
    destId: 'generic', destName: 'ACT! / SalesCrunchCRM',
    headline: 'Zoho CRM to ACT! / SalesCrunchCRM — radical simplification.',
    subhead:
      "Zoho One's 40+ apps overwhelm most small teams. SalesCrunchCRM is contact-first and " +
      "doesn't try to do email marketing, project management, AND HR. We migrate the CRM " +
      "data that matters.",
    painPoint:
      "Zoho's federated app structure spreads contact data across multiple modules. " +
      "Most tools miss half of it on export.",
    ourEdge:
      "We pull Zoho data from CRM, Books, Desk, and Campaigns — consolidating it into " +
      "one clean SalesCrunchCRM contact record per person.",
    keywords: ['zoho to act migration', 'zoho to salescrunchcrm', 'leave zoho simple crm'],
    estimatedTime: '9 minutes',
    seoTitle: 'Zoho to ACT! / SalesCrunchCRM Migration | MigrationCrunch',
    seoDescription: 'Migrate Zoho CRM to SalesCrunchCRM with multi-module data consolidation. Free to SalesCrunchCRM.',
  },
  'zoho-to-insightly': {
    slug: 'zoho-to-insightly',
    sourceId: 'zoho', sourceName: 'Zoho CRM',
    destId: 'insightly', destName: 'Insightly',
    headline: 'Zoho CRM to Insightly migration — sales + projects, one bill.',
    subhead:
      "Insightly bundles CRM and projects in one tool — useful for service teams running " +
      "Zoho CRM + Zoho Projects separately. We consolidate everything into Insightly " +
      "with note reassembly and Project auto-generation.",
    painPoint:
      "Zoho Projects data lives in a sibling app. Generic tools migrate Zoho CRM only and " +
      "lose project context.",
    ourEdge:
      "We pull both Zoho CRM and Zoho Projects, merge them under the right Insightly " +
      "records, and rebuild project-deal links automatically.",
    keywords: ['zoho to insightly migration', 'zoho crm to insightly'],
    estimatedTime: '10 minutes',
    seoTitle: 'Zoho to Insightly Migration — CRM + Projects | MigrationCrunch',
    seoDescription: 'Migrate Zoho CRM + Zoho Projects to Insightly with project-deal links restored. From $599.',
  },
  'zoho-to-copper': {
    slug: 'zoho-to-copper',
    sourceId: 'zoho', sourceName: 'Zoho CRM',
    destId: 'copper', destName: 'Copper',
    headline: 'Zoho CRM to Copper migration — Gmail-first sales motion.',
    subhead:
      "Teams on Gmail benefit from Copper's deep Workspace integration over Zoho's email " +
      "client. We migrate Contacts, Accounts, Deals, and Activities — with Zoho note " +
      "reassembly and Gmail-validation pre-flight.",
    painPoint:
      "Zoho contacts often have phantom emails from web-to-lead forms. Copper rejects them.",
    ourEdge:
      "Pre-flight validates every Zoho contact email and surfaces bounce-risk addresses " +
      "before the Copper import runs.",
    keywords: ['zoho to copper migration', 'zoho crm to copper'],
    estimatedTime: '8 minutes',
    seoTitle: 'Zoho CRM to Copper Migration — Gmail-Validated | MigrationCrunch',
    seoDescription: 'Migrate Zoho CRM to Copper with email validation and reassembled notes. From $599.',
  },
  'zoho-to-freshsales': {
    slug: 'zoho-to-freshsales',
    sourceId: 'zoho', sourceName: 'Zoho CRM',
    destId: 'freshsales', destName: 'Freshsales',
    headline: 'Zoho CRM to Freshsales migration — Indian competitor showdown.',
    subhead:
      "Both Indian-founded, both feature-rich — Freshsales tends to win on UX while Zoho " +
      "wins on price. We migrate the full Zoho schema to Freshsales with note reassembly " +
      "and structured AI scoring signals.",
    painPoint:
      "Zoho's lead score is proprietary and doesn't export. Freshsales scoring starts cold.",
    ourEdge:
      "We extract signal-rich entities from Zoho notes — decision-maker mentions, intent " +
      "language, competitor mentions — so Freshsales' AI can score from day one.",
    keywords: ['zoho to freshsales migration', 'zoho crm to freshworks crm'],
    estimatedTime: '9 minutes',
    seoTitle: 'Zoho CRM to Freshsales Migration — AI-Ready | MigrationCrunch',
    seoDescription: 'Migrate Zoho CRM to Freshsales / Freshworks with AI scoring signals extracted. From $599.',
  },
  'zoho-to-keap': {
    slug: 'zoho-to-keap',
    sourceId: 'zoho', sourceName: 'Zoho CRM',
    destId: 'keap', destName: 'Keap',
    headline: 'Zoho CRM to Keap migration — sales + automation in one.',
    subhead:
      "Keap (Infusionsoft) packs sales pipeline + email automation + e-commerce into one " +
      "license — replacing Zoho CRM + Zoho Campaigns + Zoho Commerce. We migrate them all.",
    painPoint:
      "Zoho's spread across CRM and Campaigns means contact lists live in different places " +
      "and don't merge cleanly during a migration.",
    ourEdge:
      "We deduplicate Zoho contacts across modules, then map each to Keap with the right " +
      "automation tags based on cross-module engagement history.",
    keywords: ['zoho to keap migration', 'zoho to infusionsoft'],
    estimatedTime: '10 minutes',
    seoTitle: 'Zoho to Keap Migration — Multi-Module Consolidation | MigrationCrunch',
    seoDescription: 'Migrate Zoho CRM + Campaigns to Keap with cross-module deduplication and tag mapping. From $599.',
  },
  'zoho-to-capsule': {
    slug: 'zoho-to-capsule',
    sourceId: 'zoho', sourceName: 'Zoho CRM',
    destId: 'capsule', destName: 'Capsule CRM',
    headline: 'Zoho CRM to Capsule CRM migration — escape the Zoho One sprawl.',
    subhead:
      "Capsule is what Zoho One isn't: focused, fast, single-purpose. We extract the CRM data " +
      "from your Zoho footprint and move only that — leaving the 40-app sprawl behind.",
    painPoint:
      "Capsule's free tier caps at 250 contacts; Zoho CRM databases are usually much larger.",
    ourEdge:
      "Our AI ranks Zoho contacts by recent activity. You decide whether to upgrade Capsule " +
      "or trim the long tail with confidence.",
    keywords: ['zoho to capsule crm migration', 'zoho crm to capsule'],
    estimatedTime: '7 minutes',
    seoTitle: 'Zoho CRM to Capsule CRM Migration | MigrationCrunch',
    seoDescription: 'Migrate Zoho CRM to Capsule CRM with AI-ranked contact prioritization. From $599.',
  },
  'zoho-to-monday': {
    slug: 'zoho-to-monday',
    sourceId: 'zoho', sourceName: 'Zoho CRM',
    destId: 'monday', destName: 'monday.com',
    headline: 'Zoho CRM to monday.com migration — your shape, your stages.',
    subhead:
      "monday.com's flexible board model lets you design the CRM Zoho never quite let you " +
      "build. We migrate everything in — with the right column types and reassembled notes.",
    painPoint:
      "Zoho's reporting modules don't export as visualizations. Teams lose dashboard " +
      "fluency during a board-based CRM switch.",
    ourEdge:
      "We auto-generate monday dashboards (pipeline by stage, contacts by owner, activity " +
      "by week) seeded with your migrated Zoho data.",
    keywords: ['zoho to monday com migration', 'zoho crm to monday'],
    estimatedTime: '10 minutes',
    seoTitle: 'Zoho to monday.com Migration — Dashboards Included | MigrationCrunch',
    seoDescription: 'Migrate Zoho CRM to monday.com with auto-generated dashboards and reassembled notes. From $599.',
  },
  'zoho-to-less-annoying': {
    slug: 'zoho-to-less-annoying',
    sourceId: 'zoho', sourceName: 'Zoho CRM',
    destId: 'generic', destName: 'Less Annoying CRM',
    headline: 'Zoho CRM to Less Annoying CRM migration — the deep exhale.',
    subhead:
      "LACRM is what Zoho One would be if Zoho One had any restraint. We migrate the Zoho " +
      "data you actually need and structure your notes for instant LACRM searchability.",
    painPoint:
      "LACRM's simplicity feels alarming after Zoho's 40-app sprawl until you realize how " +
      "much of that sprawl you never used.",
    ourEdge:
      "Our AI audit shows you what Zoho modules you actually used in the past year — " +
      "usually 2-3 out of 40. Migrate those, archive the rest cleanly.",
    keywords: ['zoho to less annoying crm migration', 'zoho to lacrm'],
    estimatedTime: '7 minutes',
    seoTitle: 'Zoho CRM to Less Annoying CRM Migration | MigrationCrunch',
    seoDescription: 'Migrate Zoho CRM to Less Annoying CRM with AI usage audit. From $599.',
  },
};

// ═══════════════════════════════════════════════════════════════
//  VOCABULARY TRANSLATION — every CRM calls the same objects by
//  different names. Each entry defines that CRM's terminology model;
//  getVocabMap() generates the source→dest translation table for any
//  pair automatically, so all 56 combinations are covered without
//  56 hand-written blocks. Keyed by the slug segment (sourceId for
//  sources, the part after "-to-" for destinations).
// ═══════════════════════════════════════════════════════════════
const CRM_VOCAB = {
  act: {
    person: 'Contact', org: 'Company', deal: 'Opportunity',
    activity: 'Activity (Call / Meeting / To-do)',
    note: 'Note & History entry',
    leadAsDest: 'Contact tagged "Lead" — ACT!-style CRMs keep everyone in one contact list, no separate silo',
  },
  salesforce: {
    lead: 'Lead', person: 'Contact', org: 'Account', deal: 'Opportunity',
    activity: 'Task / Event',
    note: 'Note (ContentNote)',
    leadAsDest: 'Lead — kept in its own queue until you convert it to a Contact + Account',
  },
  hubspot: {
    person: 'Contact', org: 'Company', deal: 'Deal',
    activity: 'Engagement (Call / Email / Meeting / Task)',
    note: 'Note engagement',
    leadAsDest: 'Contact with Lifecycle Stage = "Lead" — HubSpot runs one database and tracks qualification by stage',
  },
  pipedrive: {
    person: 'Person', org: 'Organization', deal: 'Deal',
    activity: 'Activity',
    note: 'Note',
    leadAsDest: 'Lead in the Leads Inbox — separate from Persons until qualified',
  },
  zoho: {
    lead: 'Lead', person: 'Contact', org: 'Account', deal: 'Deal',
    activity: 'Task / Call / Event',
    note: 'Note',
    leadAsDest: 'Lead — lives in the Leads module until converted',
  },
  insightly: {
    person: 'Contact', org: 'Organization', deal: 'Opportunity',
    activity: 'Task / Event',
    note: 'Note',
    leadAsDest: 'Lead — Insightly keeps Leads separate until converted',
  },
  copper: {
    person: 'Person', org: 'Company', deal: 'Opportunity',
    activity: 'Activity',
    note: 'Note',
    leadAsDest: 'Lead — Copper keeps Leads separate from People',
  },
  capsule: {
    person: 'Person', org: 'Organisation', deal: 'Opportunity',
    activity: 'Task',
    note: 'Note (history entry)',
    leadAsDest: 'Person tagged "prospect" — Capsule has no separate Lead object',
  },
  freshsales: {
    person: 'Contact', org: 'Account', deal: 'Deal',
    activity: 'Task / Appointment',
    note: 'Note',
    leadAsDest: 'Contact with lifecycle stage "Lead" — Freshsales retired its separate Leads module',
  },
  keap: {
    person: 'Contact', org: 'Company', deal: 'Opportunity',
    activity: 'Task / Appointment',
    note: 'Note',
    leadAsDest: 'Contact tagged "prospect" so Keap automations can fire on it',
  },
  monday: {
    person: 'Item on the Contacts board', org: 'Item on the Accounts board', deal: 'Item on the Deals board',
    activity: 'Update / Activity column',
    note: 'Update (item timeline)',
    leadAsDest: 'Item on a Leads board',
  },
  'less-annoying': {
    person: 'Contact', org: 'Company', deal: 'Pipeline item',
    activity: 'Event / Task',
    note: 'Note (contact history)',
    leadAsDest: 'Contact in a "Leads" pipeline — LACRM uses pipelines, not a Lead object',
  },
  generic: {
    person: 'Contact row', org: 'Company column', deal: 'Deal / Opportunity row',
    activity: 'Activity row',
    note: 'Structured note column',
    leadAsDest: 'Row flagged in a "lead_status" column so any CRM can filter on it',
  },
};

// Hand-written extra guidance for the highest-traffic pairs. Rendered
// under the generated table as a "Worth knowing" callout.
const VOCAB_NOTES = {
  'salesforce-to-hubspot':
    "Salesforce keeps Leads and Contacts in two separate silos with different fields; HubSpot runs one " +
    "Contacts database and tracks qualification with Lifecycle Stages. Our AI merges your Salesforce Leads " +
    "and Contacts into single HubSpot Contact records and sets the Lifecycle Stage from your Lead Status — " +
    "so no prospect gets silently promoted (or demoted) during the move.",
  'hubspot-to-salesforce':
    "HubSpot's single Contacts database has to split back into Salesforce's Lead vs Contact silos. " +
    "We use your HubSpot Lifecycle Stage as the divider: anything at Lead / MQL / SQL becomes a Salesforce " +
    "Lead; anything at Opportunity stage or beyond becomes a Contact under its Account.",
  'act-to-salesforce':
    "ACT! has no Lead concept — everyone is a Contact. We classify your ACT! contacts by opportunity " +
    "history: contacts with no open or won opportunities become Salesforce Leads; active ones become " +
    "Contacts under their Accounts. Nothing arrives as an unfiled blob.",
  'act-to-hubspot':
    "ACT!'s Notes and Histories both map to HubSpot engagements — but Histories carry a type (call, " +
    "meeting, letter) that we preserve as the matching HubSpot engagement type instead of flattening " +
    "everything into generic notes. Your call history stays call history.",
  'salesforce-to-pipedrive':
    "Salesforce Accounts become Pipedrive Organizations, but Salesforce's parent/child Account hierarchy " +
    "flattens on the way over. We preserve the hierarchy in a custom Organization field so no reporting " +
    "rollup silently disappears.",
  'pipedrive-to-hubspot':
    "Pipedrive stores names in one combined field; HubSpot needs First and Last split back apart — we do " +
    "that automatically. Pipedrive Activities also map to typed HubSpot engagements (call → Call, " +
    "meeting → Meeting) instead of arriving as untyped notes.",
  'zoho-to-salesforce':
    "Zoho and Salesforce both use the Lead / Contact / Account model, so the object mapping is nearly " +
    "1:1 — the real risk is Zoho's 32k-character note truncation. We detect Zoho's continuation-record " +
    "pattern and stitch the chunks back into complete Salesforce notes before import.",
  'zoho-to-hubspot':
    "Zoho Leads and their converted Contact records arrive in HubSpot merged into a single Contact with " +
    "the full conversion timeline intact — HubSpot's Lifecycle Stage carries the Lead-vs-Contact " +
    "distinction Zoho kept in separate modules.",
  'hubspot-to-pipedrive':
    "HubSpot's typed engagements (calls, emails, meetings) map onto Pipedrive's single Activity object " +
    "with the type preserved as the Activity kind — so your calendar of past touchpoints survives with " +
    "its structure, not as a wall of undifferentiated notes.",
  'pipedrive-to-salesforce':
    "Pipedrive has no Lead/Contact split — every Person is a Person. We classify each one by deal-stage " +
    "history: Persons with no open or won Deals become Salesforce Leads; the rest become Contacts under " +
    "their Accounts, with every Activity re-attached.",
};

// Returns { intro, rows: [{concept, from, to}], extra } for a pair,
// or null when either side has no vocabulary model.
export function getVocabMap(pair) {
  if (!pair) return null;
  const src = CRM_VOCAB[pair.sourceId];
  const destKey = pair.slug.split('-to-')[1];
  const dest = CRM_VOCAB[destKey];
  if (!src || !dest) return null;
  const rows = [];
  if (src.lead) rows.push({ concept: 'Prospects', from: src.lead, to: dest.leadAsDest });
  rows.push({ concept: 'People', from: src.person, to: dest.person });
  rows.push({ concept: 'Companies', from: src.org, to: dest.org });
  rows.push({ concept: 'Deals', from: src.deal, to: dest.deal });
  rows.push({ concept: 'Activities', from: src.activity, to: dest.activity });
  rows.push({ concept: 'Notes', from: src.note, to: dest.note });
  const intro =
    `${pair.sourceName} and ${pair.destName} don't call the same things by the same names. ` +
    `What ${pair.sourceName} calls a “${src.person}”, ${pair.destName} calls a “${dest.person}”; ` +
    `“${src.org}” becomes “${dest.org}”, and every “${src.deal}” lands as a “${dest.deal}”. ` +
    `Our AI applies the full translation below automatically during field mapping — nothing to remap by hand.`;
  return { intro, rows, extra: VOCAB_NOTES[pair.slug] || null };
}

// Convenience: lookup helper used by the route component.
export function getPair(slug) {
  return MIGRATION_PAIRS[slug] || null;
}

// All slugs (for sitemap generation + footer link grid).
export const ALL_PAIR_SLUGS = Object.keys(MIGRATION_PAIRS);
